import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../components/ui/sheet';
import {
  AlertTriangle, Package, FileWarning, MessageSquare, FileText,
  Truck, Clock, Hourglass, ChevronRight, RefreshCw, Activity, Beaker,
  CheckCircle2, X, ArrowRight, AlertCircle, Gem, Link2, Scale, Sparkles, Loader2,
} from 'lucide-react';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtTimeAgo = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ─── Action Items Strip ───
const ActionItemTile = ({ count, label, icon: Icon, color, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className={cn(
      "flex-1 min-w-[180px] rounded-xl border p-3.5 text-left transition-all hover:shadow-md hover:-translate-y-0.5 disabled:cursor-default disabled:hover:translate-y-0 disabled:hover:shadow-none",
      color
    )}
    data-testid={`action-tile-${label.toLowerCase().replace(/\s/g, '-')}`}
  >
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-background/60 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold tabular-nums leading-none">{count}</p>
        <p className="text-[11px] uppercase tracking-wider font-semibold opacity-80 mt-0.5">{label}</p>
      </div>
    </div>
  </button>
);

const ActionItemsRow = ({ items, navigate }) => {
  const total = items.pending_acceptance + items.materials_overdue + items.open_factory_requests + items.invoices_ready_to_send;
  if (total === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/60 dark:bg-emerald-500/5 dark:border-emerald-500/30">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <p className="font-bold text-emerald-900 dark:text-emerald-200">All clear</p>
            <p className="text-xs text-emerald-700 dark:text-emerald-400">No urgent action items right now.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="flex flex-wrap gap-3">
      {items.pending_acceptance > 0 && (
        <ActionItemTile
          count={items.pending_acceptance}
          label="Awaiting Factory Acceptance"
          icon={Hourglass}
          color="border-amber-300 bg-amber-50 text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-100"
          onClick={() => navigate('/orders?filter=pending_acceptance')}
        />
      )}
      {items.materials_overdue > 0 && (
        <ActionItemTile
          count={items.materials_overdue}
          label="Materials Issuance Overdue"
          icon={AlertTriangle}
          color="border-orange-300 bg-orange-50 text-orange-900 dark:bg-orange-500/10 dark:border-orange-500/40 dark:text-orange-100"
          onClick={() => navigate('/orders?filter=materials_overdue')}
        />
      )}
      {items.open_factory_requests > 0 && (
        <ActionItemTile
          count={items.open_factory_requests}
          label="Open Factory Requests"
          icon={MessageSquare}
          color="border-violet-300 bg-violet-50 text-violet-900 dark:bg-violet-500/10 dark:border-violet-500/40 dark:text-violet-100"
          onClick={() => navigate('/orders?filter=open_requests')}
        />
      )}
      {items.invoices_ready_to_send > 0 && (
        <ActionItemTile
          count={items.invoices_ready_to_send}
          label="Invoices Ready to Send"
          icon={FileText}
          color="border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-500/40 dark:text-emerald-100"
          onClick={() => navigate('/orders?filter=ready_invoice')}
        />
      )}
    </div>
  );
};

// ─── Order Management Row ───
const OrderMgmtCard = ({ title, count, icon: Icon, tone, hint, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-xl border p-4 text-left transition-all hover:shadow-md hover:-translate-y-0.5 group",
      tone
    )}
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-bold opacity-80">{title}</p>
        <p className="text-3xl font-bold tabular-nums mt-1">{count}</p>
        {hint && <p className="text-[11px] opacity-70 mt-1">{hint}</p>}
      </div>
      <div className="h-10 w-10 rounded-lg bg-background/60 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
    </div>
    <p className="text-[11px] mt-3 flex items-center gap-1 opacity-70 group-hover:opacity-100">
      View list <ChevronRight className="h-3 w-3" />
    </p>
  </button>
);

const OrderListDrawer = ({ open, onClose, title, subtitle, orders, kind, navigate }) => (
  <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid={`drawer-${kind}`}>
      <SheetHeader>
        <SheetTitle>{title}</SheetTitle>
        {subtitle && <SheetDescription>{subtitle}</SheetDescription>}
      </SheetHeader>
      <div className="mt-4 space-y-2">
        {orders.length === 0 && (
          <p className="text-center py-12 text-sm text-muted-foreground">Nothing to show here.</p>
        )}
        {orders.map(o => (
          <button
            key={o.id}
            onClick={() => { navigate(`/orders/${o.id}`); onClose(); }}
            className="w-full text-left rounded-lg border p-3 hover:bg-muted/50 transition-colors"
            data-testid={`drawer-row-${o.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-sm truncate">
                  {o.order_number}
                  {o.priority === 'Urgent' && <Badge variant="destructive" className="ml-2 text-[9px] h-4 px-1">URGENT</Badge>}
                </p>
                <p className="text-xs text-muted-foreground truncate">{o.customer_name}{o.factory_name ? ` · ${o.factory_name}` : ''}</p>
              </div>
              <div className="text-right flex-shrink-0">
                {kind === 'delayed' && o.days_overdue != null && (
                  <p className="text-sm font-bold text-rose-600 dark:text-rose-400 tabular-nums">{o.days_overdue}d late</p>
                )}
                {(kind === 'ready' || kind === 'awaiting') && (
                  <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                    {o.ready_pcs}/{o.pieces_ordered}
                  </p>
                )}
                {o.delivery_deadline && (
                  <p className="text-[10px] text-muted-foreground">Deadline {formatDate(o.delivery_deadline)}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

const OrderMgmtRow = ({ om, navigate }) => {
  const [drawer, setDrawer] = useState(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <OrderMgmtCard
        title="Ready to Ship"
        count={om.ready_to_ship.length}
        hint="Some pieces ready, others still in production"
        icon={Truck}
        tone="border-emerald-200 bg-emerald-50 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-500/40 dark:text-emerald-100"
        onClick={() => setDrawer('ready')}
        testId="card-ready-to-ship"
      />
      <OrderMgmtCard
        title="Awaiting Customer Confirmation"
        count={om.awaiting_customer.length}
        hint="All pieces ready — waiting on customer to confirm dispatch"
        icon={Clock}
        tone="border-blue-200 bg-blue-50 text-blue-900 dark:bg-blue-500/10 dark:border-blue-500/40 dark:text-blue-100"
        onClick={() => setDrawer('awaiting')}
        testId="card-awaiting-customer"
      />
      <OrderMgmtCard
        title="Delayed"
        count={om.delayed.length}
        hint="Past delivery deadline, production still pending"
        icon={AlertCircle}
        tone="border-rose-200 bg-rose-50 text-rose-900 dark:bg-rose-500/10 dark:border-rose-500/40 dark:text-rose-100"
        onClick={() => setDrawer('delayed')}
        testId="card-delayed"
      />

      <OrderListDrawer
        open={drawer === 'ready'}
        kind="ready"
        onClose={() => setDrawer(null)}
        title="Ready to Ship (partial)"
        subtitle="At least one piece is in the Ready to Ship stage."
        orders={om.ready_to_ship}
        navigate={navigate}
      />
      <OrderListDrawer
        open={drawer === 'awaiting'}
        kind="awaiting"
        onClose={() => setDrawer(null)}
        title="Awaiting Customer Confirmation"
        subtitle="All pieces are ready. We're holding for the customer to confirm dispatch."
        orders={om.awaiting_customer}
        navigate={navigate}
      />
      <OrderListDrawer
        open={drawer === 'delayed'}
        kind="delayed"
        onClose={() => setDrawer(null)}
        title="Delayed orders"
        subtitle="These are genuinely behind schedule — production hasn't finished and the deadline has passed."
        orders={om.delayed}
        navigate={navigate}
      />
    </div>
  );
};

// ─── Funnel Card ───
const Funnel = ({ title, subtitle, data, accent, testId }) => {
  const max = Math.max(1, ...data.map(d => d.pieces));
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base">{title}</CardTitle>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent className="space-y-1.5">
        {data.map((d) => {
          const width = Math.max(2, (d.pieces / max) * 100);
          const isBlocker = d.is_blocker;
          return (
            <div key={d.stage} className="group" data-testid={`funnel-row-${d.stage}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={cn(
                  "text-[11px] font-semibold uppercase tracking-wider",
                  isBlocker ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                )}>
                  {d.label}
                </span>
                <span className={cn(
                  "text-sm font-bold tabular-nums",
                  isBlocker && d.pieces > 0 ? 'text-amber-700 dark:text-amber-400' : ''
                )}>
                  {d.pieces}
                </span>
              </div>
              <div className="h-6 rounded-md bg-muted/40 overflow-hidden">
                <div
                  className={cn("h-full transition-all rounded-md", isBlocker ? 'bg-amber-400 dark:bg-amber-500' : accent)}
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ─── Gold Karat Breakdown Card ───
const GoldKaratCard = ({ pm }) => {
  const byKarat = pm?.gold_by_karat || {};
  const entries = Object.entries(byKarat).filter(([, d]) => (d.required || 0) > 0);
  if (entries.length === 0) return null;
  const totalPending24 = pm?.total_pending_24kt || 0;
  return (
    <Card data-testid="gold-karat-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Scale className="h-4 w-4 text-amber-600 dark:text-amber-400" /> Gold Requirement — Karat Breakdown
        </CardTitle>
        <p className="text-xs text-muted-foreground">Pending issuance per karat, converted to 24kt pure gold equivalent</p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {entries
            .sort((a, b) => b[1].pending_24kt - a[1].pending_24kt)
            .map(([karat, d]) => (
              <div
                key={karat}
                className="p-3 rounded-xl border border-yellow-200/60 dark:border-yellow-500/30 bg-yellow-50/60 dark:bg-yellow-500/5"
                data-testid={`gold-karat-${karat}`}
              >
                <p className="text-[11px] text-yellow-700 dark:text-yellow-400 font-bold uppercase tracking-wider">{karat.toUpperCase()}</p>
                <p className="text-xl font-bold text-yellow-800 dark:text-yellow-200 tabular-nums mt-1">
                  {d.pending}<span className="text-xs font-medium ml-0.5">g</span>
                </p>
                <p className="text-[10px] text-muted-foreground">{d.pending_24kt}g in 24kt</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {d.issued}g / {d.required}g
                </p>
              </div>
            ))}
          <div
            className="p-3 rounded-xl border border-amber-300/70 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10"
            data-testid="gold-karat-total"
          >
            <p className="text-[11px] text-amber-700 dark:text-amber-300 font-bold uppercase tracking-wider">Total 24KT</p>
            <p className="text-xl font-bold text-amber-800 dark:text-amber-200 tabular-nums mt-1">
              {totalPending24}<span className="text-xs font-medium ml-0.5">g</span>
            </p>
            <p className="text-[10px] text-muted-foreground">Pure gold equivalent</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Materials Status Card ───
const MaterialsCard = ({ pm, navigate }) => {
  const gold = pm?.gold || { total_required: 0, total_issued: 0, pending: 0 };
  const goldPct = gold.total_required > 0
    ? Math.min(100, Math.round((gold.total_issued / gold.total_required) * 100))
    : 0;
  const diamondsCount = pm?.diamonds?.pending_orders || 0;
  const chainsCount = pm?.chains?.pending_orders || 0;
  return (
    <Card data-testid="materials-status">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Package className="h-4 w-4" /> Materials Status
        </CardTitle>
        <p className="text-xs text-muted-foreground">Issuance overview across active orders</p>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Gold */}
        <button
          type="button"
          onClick={() => navigate('/metal-ledger')}
          className="text-left p-3 rounded-lg border border-amber-200/60 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/5 hover:shadow-md transition-all"
          data-testid="materials-gold"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center">
                <span className="text-amber-600 dark:text-amber-400 font-bold text-[10px]">Au</span>
              </div>
              <span className="text-sm font-semibold">Gold</span>
            </div>
            <span className="text-xl font-bold text-amber-700 dark:text-amber-400 tabular-nums">
              {gold.pending}<span className="text-xs ml-0.5 opacity-70">g pending</span>
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-amber-100 dark:bg-amber-500/10 overflow-hidden">
            <div
              className="h-full bg-amber-500 dark:bg-amber-400 transition-all"
              style={{ width: `${goldPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>Issued {gold.total_issued}g</span>
            <span>Required {gold.total_required}g</span>
          </div>
        </button>
        {/* Diamonds */}
        <button
          type="button"
          onClick={() => navigate('/orders?filter=diamonds_pending')}
          className="text-left p-3 rounded-lg border border-sky-200/60 dark:border-sky-500/30 bg-sky-50/60 dark:bg-sky-500/5 hover:shadow-md transition-all"
          data-testid="materials-diamonds"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-sky-100 dark:bg-sky-500/20 flex items-center justify-center">
                <Gem className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
              </div>
              <span className="text-sm font-semibold">Diamonds</span>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-sky-700 dark:text-sky-400 tabular-nums">{diamondsCount}</p>
              <p className="text-[10px] text-muted-foreground">orders awaiting</p>
            </div>
          </div>
        </button>
        {/* Chains */}
        <button
          type="button"
          onClick={() => navigate('/chain-inventory')}
          className="text-left p-3 rounded-lg border border-slate-200/60 dark:border-neutral-700 bg-slate-50/60 dark:bg-neutral-800/40 hover:shadow-md transition-all"
          data-testid="materials-chains"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-full bg-slate-200 dark:bg-neutral-700 flex items-center justify-center">
                <Link2 className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" />
              </div>
              <span className="text-sm font-semibold">Chains</span>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-slate-700 dark:text-slate-200 tabular-nums">{chainsCount}</p>
              <p className="text-[10px] text-muted-foreground">orders awaiting</p>
            </div>
          </div>
        </button>
      </CardContent>
    </Card>
  );
};

// ─── Certification Forecast Embed ───
const CertForecastCard = ({ navigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/labs/forecast`)
      .then(r => setData(r.data))
      .catch(() => setData({ labs: {} }))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <Skeleton className="h-40 w-full" />;
  const labs = data?.labs || {};
  const entries = Object.entries(labs);
  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Beaker className="h-4 w-4" /> Certification Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No orders currently need certification planning.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Beaker className="h-4 w-4" /> Certification Forecast
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={() => navigate('/lab-planner')} data-testid="open-lab-planner">
            Open planner <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {entries.map(([lab, info]) => (
          <button
            key={lab}
            type="button"
            onClick={() => navigate('/lab-planner')}
            className="rounded-lg border p-3 text-left hover:bg-muted/40 transition-colors"
            data-testid={`cert-lab-${lab}`}
          >
            <p className="text-sm font-bold">{lab}</p>
            <p className="text-2xl font-bold tabular-nums mt-1">
              {info.ready_now_pcs || 0}
              <span className="text-xs text-muted-foreground font-medium ml-1">ready now</span>
            </p>
            <p className="text-[11px] text-muted-foreground">{(info.orders || []).length} order(s) in queue</p>
          </button>
        ))}
      </CardContent>
    </Card>
  );
};

// ─── Activity Feed ───
const MOVEMENT_LABEL = {
  stage_movement: 'Stage movement',
  material_issuance: 'Material issued',
  factory_accepted: 'Factory accepted',
  factory_rejected: 'Factory rejected',
  factory_acceptance_reset: 'Re-sent to factory',
  factory_dismissed: 'Hidden from factory',
  order_created: 'Order created',
  payment_recorded: 'Payment recorded',
  dispatch_created: 'Dispatch created',
  dispatch_edited: 'Dispatch edited',
};
const ActivityFeed = ({ items, navigate }) => (
  <Card>
    <CardHeader className="pb-2">
      <CardTitle className="font-heading text-base flex items-center gap-2">
        <Activity className="h-4 w-4" /> Recent Activity
      </CardTitle>
    </CardHeader>
    <CardContent>
      <ScrollArea className="h-80">
        <div className="space-y-1">
          {items.length === 0 && (
            <p className="text-center py-12 text-sm text-muted-foreground">No recent activity.</p>
          )}
          {items.map((m, i) => (
            <button
              key={i}
              type="button"
              onClick={() => navigate(`/orders/${m.order_id}`)}
              className="w-full text-left flex items-start gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors"
              data-testid={`activity-row-${i}`}
            >
              <div className="h-2 w-2 rounded-full bg-violet-400 dark:bg-violet-500 mt-1.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs">
                  <span className="font-semibold">{m.order_number}</span>
                  <span className="text-muted-foreground"> · {m.customer_name || ''}</span>
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                  {MOVEMENT_LABEL[m.movement_type] || m.movement_type}
                  {m.from_stage && m.to_stage ? ` · ${m.from_stage} → ${m.to_stage}` : ''}
                  {m.pieces ? ` (${m.pieces} pcs)` : ''}
                  {m.user_name ? ` · by ${m.user_name}` : ''}
                </p>
                {m.notes && <p className="text-[11px] text-muted-foreground/80 mt-0.5 line-clamp-1 italic">{m.notes}</p>}
              </div>
              <span className="text-[10px] text-muted-foreground flex-shrink-0">{fmtTimeAgo(m.timestamp)}</span>
            </button>
          ))}
        </div>
      </ScrollArea>
    </CardContent>
  </Card>
);

// ─── AI Ops Insights (daily briefing) ───
const healthToTone = (h) => ({
  on_track: { label: 'On track', cls: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
  ready_to_ship: { label: 'Ready', cls: 'border-sky-300 bg-sky-50 text-sky-700' },
  at_risk: { label: 'At risk', cls: 'border-amber-300 bg-amber-50 text-amber-700' },
  critical: { label: 'Critical', cls: 'border-rose-300 bg-rose-50 text-rose-700' },
  blocked: { label: 'Blocked', cls: 'border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700' },
}[h] || { label: h || '—', cls: 'border-slate-300 bg-slate-50 text-slate-600' });

const priorityDot = (p) => ({
  urgent: 'bg-rose-500',
  high: 'bg-amber-500',
  medium: 'bg-sky-500',
  routine: 'bg-slate-300',
}[p] || 'bg-slate-300');

const OpsInsightsCard = ({ navigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [healthFilter, setHealthFilter] = useState('all');

  const fetchToday = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/ops/insights/today`);
      setData(r.data);
      if (r.data?.status === 'generating') setGenerating(true);
      else setGenerating(false);
    } catch {
      // silent fail — card will show "unavailable"
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  // Poll while generating
  useEffect(() => {
    if (!generating) return;
    const id = setInterval(fetchToday, 4000);
    return () => clearInterval(id);
  }, [generating, fetchToday]);

  const kickOff = async () => {
    try {
      setGenerating(true);
      await axios.post(`${API_URL}/ops/insights/generate`);
      toast.success("Briefing started — this takes 20-40 seconds");
      fetchToday();
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || 'Failed to kick off briefing');
    }
  };

  const score = data?.health_score;
  const scoreTone = score == null ? 'text-slate-500' : score >= 90 ? 'text-emerald-600' : score >= 70 ? 'text-amber-600' : 'text-rose-600';
  const scoreBg = score == null ? 'bg-slate-100' : score >= 90 ? 'bg-emerald-50' : score >= 70 ? 'bg-amber-50' : 'bg-rose-50';

  // Group orders by health for the filter chips
  const grouped = useMemo(() => {
    const g = { all: data?.orders_review || [] };
    (data?.orders_review || []).forEach(o => {
      g[o.health] = (g[o.health] || []);
      g[o.health].push(o);
    });
    return g;
  }, [data]);
  const visible = grouped[healthFilter] || [];

  // ─ loading skeleton
  if (loading) {
    return <Skeleton className="h-40 w-full rounded-2xl" />;
  }

  // ─ empty / never generated
  const empty = !data || data.status === 'empty';
  const failed = data?.status === 'failed';
  const isGenerating = data?.status === 'generating' || generating;

  return (
    <div className="rounded-2xl border bg-gradient-to-br from-violet-50 via-indigo-50 to-sky-50 dark:from-violet-500/10 dark:via-indigo-500/10 dark:to-sky-500/10 overflow-hidden" data-testid="ops-insights-card">
      <div className="p-4 md:p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-600 to-sky-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold uppercase tracking-wider text-violet-900 dark:text-violet-200">AI Production Briefing</h2>
              {data?.generated_at && !isGenerating && (
                <Badge variant="outline" className="text-[10px] bg-white/70">
                  <Clock className="h-2.5 w-2.5 mr-1" /> {fmtTimeAgo(data.generated_at)}
                </Badge>
              )}
            </div>
            {empty && (
              <p className="text-sm text-muted-foreground mt-2">
                Get an AI-generated review of every active order — as if your production floor manager just briefed you.
              </p>
            )}
            {failed && (
              <p className="text-xs text-rose-700 mt-1">⚠ {data.error || 'Generation failed'} — click Regenerate.</p>
            )}
            {isGenerating && (
              <p className="text-xs text-indigo-700 mt-1 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Walking the floor — this takes 20-40 seconds…
              </p>
            )}
            {data?.headline && !empty && !isGenerating && (
              <p className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-100 mt-1 leading-snug">
                {data.headline}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {score != null && !empty && (
              <div className={cn("flex flex-col items-center justify-center h-14 w-14 rounded-xl border-2", scoreBg, scoreTone, "border-current/20")}
                   title="Overall health score">
                <p className="text-xl font-bold leading-none tabular-nums">{score}</p>
                <p className="text-[9px] uppercase tracking-wider opacity-70 mt-0.5">health</p>
              </div>
            )}
            <Button size="sm" variant="outline" onClick={kickOff} disabled={isGenerating}
                    className="h-9 bg-white/80 hover:bg-white" data-testid="generate-insights-btn">
              {isGenerating
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Working…</>
                : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> {empty ? 'Generate' : 'Regenerate'}</>}
            </Button>
          </div>
        </div>

        {data?.executive_summary && !empty && !isGenerating && (
          <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed border-l-4 border-violet-400 pl-3 py-1">
            {data.executive_summary}
          </p>
        )}

        {/* Red alerts + Green flags */}
        {(data?.red_alerts?.length > 0 || data?.green_flags?.length > 0) && !isGenerating && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
            {data.red_alerts?.length > 0 && (
              <div className="rounded-xl bg-rose-50/90 border border-rose-200 p-3" data-testid="insights-red-alerts">
                <p className="text-[11px] font-bold uppercase tracking-wider text-rose-800 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Red alerts ({data.red_alerts.length})
                </p>
                <ul className="mt-1.5 space-y-1 text-xs text-rose-900">
                  {data.red_alerts.map((a, i) => <li key={i} className="flex gap-1.5"><span className="text-rose-500 mt-0.5">▸</span>{a}</li>)}
                </ul>
              </div>
            )}
            {data.green_flags?.length > 0 && (
              <div className="rounded-xl bg-emerald-50/90 border border-emerald-200 p-3" data-testid="insights-green-flags">
                <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Green flags ({data.green_flags.length})
                </p>
                <ul className="mt-1.5 space-y-1 text-xs text-emerald-900">
                  {data.green_flags.map((a, i) => <li key={i} className="flex gap-1.5"><span className="text-emerald-500 mt-0.5">▸</span>{a}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Per-order review */}
      {data?.orders_review?.length > 0 && !isGenerating && (
        <div className="border-t bg-white/60 dark:bg-neutral-950/40">
          <button type="button" onClick={() => setExpanded(e => !e)}
                   className="w-full px-4 md:px-5 py-2.5 flex items-center justify-between text-sm font-semibold hover:bg-white/50 transition-colors"
                   data-testid="insights-expand-toggle">
            <span className="flex items-center gap-2">
              <Package className="h-4 w-4 text-violet-600" />
              Per-order review ({data.orders_review.length})
            </span>
            <ChevronRight className={cn("h-4 w-4 transition-transform", expanded && "rotate-90")} />
          </button>

          {expanded && (
            <>
              <div className="px-4 md:px-5 py-2 flex gap-1.5 overflow-x-auto scrollbar-hide border-b bg-white/40">
                {['all', 'critical', 'blocked', 'at_risk', 'ready_to_ship', 'on_track'].map(k => {
                  const count = grouped[k]?.length || 0;
                  if (k !== 'all' && count === 0) return null;
                  const label = k === 'all' ? 'All' : healthToTone(k).label;
                  return (
                    <button key={k} type="button" onClick={() => setHealthFilter(k)}
                             className={cn("text-[11px] px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors",
                               healthFilter === k ? "bg-violet-600 border-violet-600 text-white" : "bg-white hover:bg-slate-50 border-slate-200")}
                             data-testid={`insights-filter-${k}`}>
                      {label} <span className="opacity-70">· {count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="divide-y max-h-[480px] overflow-y-auto">
                {visible.map(o => {
                  const tone = healthToTone(o.health);
                  return (
                    <button key={o.order_id} type="button"
                             onClick={() => o.order_id && navigate(`/orders/${o.order_id}`)}
                             className="w-full text-left px-4 md:px-5 py-3 hover:bg-violet-50/50 transition-colors flex items-start gap-3 group"
                             data-testid={`insights-order-${o.order_id}`}>
                      <span className={cn("h-2 w-2 rounded-full mt-2 flex-shrink-0", priorityDot(o.priority))} title={`${o.priority} priority`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold">{o.co_number || '—'}</span>
                          <span className="text-xs text-muted-foreground truncate">{o.customer}</span>
                          <Badge variant="outline" className={cn("text-[10px] ml-auto", tone.cls)}>{tone.label}</Badge>
                        </div>
                        <p className="text-xs text-slate-700 dark:text-slate-300 mt-1 leading-snug">{o.one_liner}</p>
                        {(o.blockers?.length > 0 || o.recommended_actions?.length > 0) && (
                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                            {o.blockers?.slice(0, 3).map((b, i) => (
                              <span key={`b-${i}`} className="inline-flex items-start gap-1 text-rose-700">
                                <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /><span>{b}</span>
                              </span>
                            ))}
                            {o.recommended_actions?.slice(0, 3).map((a, i) => (
                              <span key={`a-${i}`} className="inline-flex items-start gap-1 text-sky-700">
                                <ArrowRight className="h-3 w-3 mt-0.5 flex-shrink-0" /><span>{a}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-violet-600 flex-shrink-0 mt-0.5" />
                    </button>
                  );
                })}
                {visible.length === 0 && (
                  <div className="py-6 text-center text-xs text-muted-foreground">No orders in this bucket.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Dashboard ───
const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await axios.get(`${API_URL}/ops/dashboard`);
      setData(res.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = useMemo(() => {
    if (!data) return null;
    const om = data.order_management;
    return {
      total: om.ready_to_ship.length + om.awaiting_customer.length + om.delayed.length,
    };
  }, [data]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="p-4 md:p-6 space-y-5 max-w-7xl mx-auto">
        {/* Title bar */}
        <div className="flex items-center justify-between gap-2">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold">Operations</h1>
            <p className="text-xs text-muted-foreground">
              Live snapshot across orders, factory, certification, dispatch.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="refresh-dashboard"
          >
            <RefreshCw className={cn("h-3.5 w-3.5 mr-1", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>

        {loading || !data ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <>
            {/* AI Daily Briefing */}
            <OpsInsightsCard navigate={navigate} />

            {/* Row 1 — Action Items */}
            <ActionItemsRow items={data.action_items} navigate={navigate} />

            {/* Row 2 — Order Management */}
            <OrderMgmtRow om={data.order_management} navigate={navigate} />

            {/* Row 3 — Materials Status */}
            <MaterialsCard pm={data.pending_materials} navigate={navigate} />

            {/* Row 3b — Gold Karat Breakdown */}
            <GoldKaratCard pm={data.pending_materials} />

            {/* Row 4 — Two funnels side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <Funnel
                title="Production Stages"
                subtitle="Pieces after they return from factory"
                data={data.production_funnel}
                accent="bg-violet-400 dark:bg-violet-500"
                testId="production-funnel"
              />
              <Funnel
                title="Factory Stages"
                subtitle="Pieces while factory is working on them"
                data={data.factory_funnel}
                accent="bg-blue-400 dark:bg-blue-500"
                testId="factory-funnel"
              />
            </div>

            {/* Row 5 — Certification Forecast */}
            <CertForecastCard navigate={navigate} />

            {/* Row 6 — Activity Feed */}
            <ActivityFeed items={data.activity_feed} navigate={navigate} />
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
